import { keepPreviousData } from '@tanstack/react-query';
import type { SearchEntityRecordsInfiniteQueryOptions } from '@kizen/query/records';
import { searchEntityRecordsInfiniteQuery } from 'query/records';
import { camelToSnakeCaseKeys } from '__services/helpers';

type Sort = {
  column: string;
  direction: 'asc' | 'desc';
  id?: string;
  isDefault?: boolean;
  name: string;
};

export const getInfiniteQueryForTable = (options: {
  objectId: string;
  search?: string;
  dashlet: any;
  filtersForCharts?: any;
  model: any;
  sort: Sort;
  fieldIds?: string[];
  searchWithinFieldIds: string[];
  loading: boolean;
}): ReturnType<typeof searchEntityRecordsInfiniteQuery> => {
  const {
    dashlet,
    fieldIds = [],
    searchWithinFieldIds = [],
    filtersForCharts,
    loading,
    model,
    objectId,
    search,
    sort,
  } = options;

  const enabled =
    Boolean(objectId) &&
    Boolean(model) &&
    fieldIds.length >= 0 &&
    loading === false;

  const ordering = `${sort.direction === 'desc' ? '-' : ''}${
    // Special case to handle difference in ID and same when using client vs custom object fields
    sort.isDefault ? sort.name : sort.id ?? sort.name
  }`;

  const params: SearchEntityRecordsInfiniteQueryOptions['params'] = {
    in_group_ids: dashlet?.config?.filters?.inGroupIds,
    not_in_group_ids: dashlet?.config?.filters?.notInGroupIds,
    ordering,
    page_size: 50,
    search,
  };

  const body: SearchEntityRecordsInfiniteQueryOptions['body'] = {};

  const query: { and: boolean; filters: Record<string, unknown>[] }[] = [];

  if (filtersForCharts?.query?.length) {
    query.push(camelToSnakeCaseKeys(filtersForCharts));
  }

  if (dashlet?.config?.filters?.customFilters) {
    query.push(camelToSnakeCaseKeys(dashlet?.config?.filters?.customFilters));
  }

  if (query.length) {
    body.and = true;
    body.query = query;
  }

  if (fieldIds.length) {
    body.field_ids = fieldIds;
  }

  if (searchWithinFieldIds.length) {
    body.search_within_field_ids = searchWithinFieldIds;
  }

  return searchEntityRecordsInfiniteQuery(objectId, {
    body,
    params,
    initialPageParam: 1,
    enabled,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage) => {
      if (lastPage.next) {
        return new URL(lastPage.next).searchParams.get('page');
      }
    },
    getPreviousPageParam: (previousPage) => {
      if (previousPage.previous) {
        return new URL(previousPage.previous).searchParams.get('page');
      }
    },
  });
};
